/**
 * Random Forest Classifier for Phishing Detection
 * Implements a simplified Random Forest algorithm
 */

export class DecisionTree {
  constructor(maxDepth = 10, minSamplesSplit = 2) {
    this.maxDepth = maxDepth;
    this.minSamplesSplit = minSamplesSplit;
    this.tree = null;
  }

  fit(X, y) {
    this.tree = this.buildTree(X, y, 0);
  }

  buildTree(X, y, depth) {
    const numSamples = X.length;
    const numFeatures = X[0]?.length || 0;

    // Check stopping conditions
    if (depth >= this.maxDepth || numSamples < this.minSamplesSplit || this.isPure(y)) {
      return { isLeaf: true, prediction: this.majorityVote(y), probability: this.calculateProbability(y) };
    }

    // Find best split
    const bestSplit = this.findBestSplit(X, y, numFeatures);

    if (!bestSplit) {
      return { isLeaf: true, prediction: this.majorityVote(y), probability: this.calculateProbability(y) };
    }

    // Split data
    const { leftX, leftY, rightX, rightY } = this.splitData(X, y, bestSplit.featureIndex, bestSplit.threshold);

    // Recursively build subtrees
    const leftSubtree = this.buildTree(leftX, leftY, depth + 1);
    const rightSubtree = this.buildTree(rightX, rightY, depth + 1);

    return {
      isLeaf: false,
      featureIndex: bestSplit.featureIndex,
      threshold: bestSplit.threshold,
      left: leftSubtree,
      right: rightSubtree
    };
  }

  findBestSplit(X, y, numFeatures) {
    let bestGain = -Infinity;
    let bestSplit = null;

    // Randomly select subset of features (sqrt of total)
    const featureIndices = this.randomSample(
      Array.from({ length: numFeatures }, (_, i) => i),
      Math.floor(Math.sqrt(numFeatures))
    );

    for (const featureIndex of featureIndices) {
      const values = X.map(row => row[featureIndex]);
      const uniqueValues = [...new Set(values)].sort((a, b) => a - b);

      for (let i = 0; i < uniqueValues.length - 1; i++) {
        const threshold = (uniqueValues[i] + uniqueValues[i + 1]) / 2;
        const gain = this.informationGain(X, y, featureIndex, threshold);

        if (gain > bestGain) {
          bestGain = gain;
          bestSplit = { featureIndex, threshold };
        }
      }
    }

    return bestSplit;
  }

  informationGain(X, y, featureIndex, threshold) {
    const parentEntropy = this.entropy(y);

    const { leftY, rightY } = this.splitData(X, y, featureIndex, threshold);

    if (leftY.length === 0 || rightY.length === 0) return 0;

    const n = y.length;
    const leftWeight = leftY.length / n;
    const rightWeight = rightY.length / n;

    const childEntropy = leftWeight * this.entropy(leftY) + rightWeight * this.entropy(rightY);

    return parentEntropy - childEntropy;
  }

  entropy(y) {
    const counts = {};
    for (const label of y) {
      counts[label] = (counts[label] || 0) + 1;
    }

    let entropy = 0;
    const n = y.length;
    for (const count of Object.values(counts)) {
      const p = count / n;
      if (p > 0) entropy -= p * Math.log2(p);
    }

    return entropy;
  }

  splitData(X, y, featureIndex, threshold) {
    const leftX = [], leftY = [], rightX = [], rightY = [];

    for (let i = 0; i < X.length; i++) {
      if (X[i][featureIndex] <= threshold) {
        leftX.push(X[i]);
        leftY.push(y[i]);
      } else {
        rightX.push(X[i]);
        rightY.push(y[i]);
      }
    }

    return { leftX, leftY, rightX, rightY };
  }

  isPure(y) {
    return new Set(y).size === 1;
  }

  majorityVote(y) {
    const counts = {};
    for (const label of y) {
      counts[label] = (counts[label] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 0;
  }

  calculateProbability(y) {
    const phishingCount = y.filter(label => label === 1).length;
    return phishingCount / y.length;
  }

  randomSample(arr, size) {
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(size, arr.length));
  }

  predict(x) {
    return this.traverse(x, this.tree);
  }

  predictProba(x) {
    return this.traverseProba(x, this.tree);
  }

  traverse(x, node) {
    if (node.isLeaf) return node.prediction;

    if (x[node.featureIndex] <= node.threshold) {
      return this.traverse(x, node.left);
    } else {
      return this.traverse(x, node.right);
    }
  }

  traverseProba(x, node) {
    if (node.isLeaf) return node.probability;

    if (x[node.featureIndex] <= node.threshold) {
      return this.traverseProba(x, node.left);
    } else {
      return this.traverseProba(x, node.right);
    }
  }
}

export class RandomForestClassifier {
  constructor(options = {}) {
    this.nEstimators = options.nEstimators || 100;
    this.maxDepth = options.maxDepth || 10;
    this.minSamplesSplit = options.minSamplesSplit || 2;
    this.bootstrapRatio = options.bootstrapRatio || 0.8;
    this.trees = [];
    this.featureImportances = [];
  }

  fit(X, y) {
    this.trees = [];

    for (let i = 0; i < this.nEstimators; i++) {
      // Bootstrap sampling
      const { sampleX, sampleY } = this.bootstrapSample(X, y);

      // Create and train tree
      const tree = new DecisionTree(this.maxDepth, this.minSamplesSplit);
      tree.fit(sampleX, sampleY);
      this.trees.push(tree);
    }

    return this;
  }

  bootstrapSample(X, y) {
    const n = X.length;
    const sampleSize = Math.floor(n * this.bootstrapRatio);
    const sampleX = [];
    const sampleY = [];

    for (let i = 0; i < sampleSize; i++) {
      const idx = Math.floor(Math.random() * n);
      sampleX.push(X[idx]);
      sampleY.push(y[idx]);
    }

    return { sampleX, sampleY };
  }

  predict(x) {
    const predictions = this.trees.map(tree => tree.predict(x));
    return this.majorityVote(predictions);
  }

  predictProba(x) {
    const probabilities = this.trees.map(tree => tree.predictProba(x));
    return probabilities.reduce((sum, p) => sum + p, 0) / probabilities.length;
  }

  majorityVote(predictions) {
    const counts = {};
    for (const pred of predictions) {
      counts[pred] = (counts[pred] || 0) + 1;
    }
    return parseInt(Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]);
  }

  score(X, y) {
    let correct = 0;
    for (let i = 0; i < X.length; i++) {
      if (this.predict(X[i]) === y[i]) correct++;
    }
    return correct / X.length;
  }

  toJSON() {
    return {
      nEstimators: this.nEstimators,
      maxDepth: this.maxDepth,
      minSamplesSplit: this.minSamplesSplit,
      trees: this.trees.map(tree => tree.tree)
    };
  }

  static fromJSON(json) {
    const rf = new RandomForestClassifier({
      nEstimators: json.nEstimators,
      maxDepth: json.maxDepth,
      minSamplesSplit: json.minSamplesSplit
    });

    rf.trees = json.trees.map(treeData => {
      const tree = new DecisionTree();
      tree.tree = treeData;
      return tree;
    });

    return rf;
  }
}
